import moment from 'moment-timezone';
import EnumService from '../../jwt/enum.service';
import { SAP_METODI_PAGAMENTO_VALIDI } from '../../../utils/constants/pagamentiConst';
import { isFlussoIberia } from '../../../utils/constants/flussiConst';
import { OUTCOME_DISABLED_REASON_KEYS } from './outcomeDisabledReasons';

// Stato pagamento impostato dopo l'esito "Invio ordine di pagamento SAP".
export const STATO_PAGAMENTO_INVIATO_SAP = 'INVIATO_SAP';

// Con pagamento INVIATO_SAP lo storno è consentito fino a quest'ora del giorno di invio.
export const SAP_STORNO_ORA_LIMITE = 19;

// Esiti SAP (statoEsitoSap) che sbloccano lo storno oltre le 19:00: D = divergente, R = rigettato banca
// (AFU "REVO_ATF_Integrazione OXS_SAP GL v2.6", Caso 2). C e L non cambiano il limite.
export const SAP_ESITI_STORNO_SBLOCCATO = ['D', 'R'];

// Le 19:00 sono ora italiana (legale/solare). La finestra storno Piteco in BottomBar mantiene il suo fuso.
const TIMEZONE_DATA_INVIO = 'Europe/Rome';

const hasValue = (value) => value !== undefined && value !== null && value !== '';

// Stringhe ISO 8601 (con o senza offset; senza offset = ora italiana), epoch millis o Date.
const toMomentRoma = (value) => (
  typeof value === 'string'
    ? moment.tz(value, moment.ISO_8601, TIMEZONE_DATA_INVIO)
    : moment.tz(value, TIMEZONE_DATA_INVIO)
);

const normalize = (value) => (typeof value === 'string' ? value.toLowerCase() : '');

export const isEsitoInvioOrdinePagamentoSap = (description) => (
  EnumService.ESITI.INVIO_ORDINE_PAGAMENTO_SAP.includes(normalize(description))
);

// Feature flag applicato dal BFF: SAP è attivo solo se l'esito SAP è nella lista esiti del task.
export const hasEsitoInvioOrdinePagamentoSap = (listaEsiti) => (
  Array.isArray(listaEsiti)
  && listaEsiti.some((esito) => isEsitoInvioOrdinePagamentoSap(esito?.description))
);

// Applicabilità dell'esito SAP (mai sui flussi Iberia):
// - pagamento spesa con metodo in SAP_METODI_PAGAMENTO_VALIDI;
// - pagamento indennizzo con "Pagamento Fattura" e metodo del beneficiario in SAP_METODI_PAGAMENTO_VALIDI.
export const isSapApplicable = ({
  tipoTask,
  idFlusso,
  metodoPagamentoSpesa,
  metodoPagamentoBeneficiario,
  pagamentoFattura,
} = {}) => {
  if (isFlussoIberia(idFlusso)) {
    return false;
  }
  if (tipoTask === 'PAGAMENTO_SPESA') {
    return SAP_METODI_PAGAMENTO_VALIDI.includes(metodoPagamentoSpesa);
  }
  if (tipoTask === 'PAGAMENTO_INDENNIZZO') {
    return pagamentoFattura === true && SAP_METODI_PAGAMENTO_VALIDI.includes(metodoPagamentoBeneficiario);
  }
  return false;
};

// "Invio ordine di pagamento SAP" segue le stesse regole di "Invio ordine di pagamento"
// (authority, pagamento istantaneo): unico helper riusato da profile.validation e dagli hook.
export const isEsitoInvioOrdinePagamentoOSap = (description) => {
  const normalized = normalize(description);
  return EnumService.ESITI.INVIO_ORDINE_PAGAMENTO.includes(normalized)
    || EnumService.ESITI.INVIO_ORDINE_PAGAMENTO_SAP.includes(normalized);
};

// SAP attivo = esito SAP esposto dal BFF nella lista esiti del task (feature flag) e pagamento applicabile.
export const computeIsSapAttivo = ({ listaEsiti, ...applicabilita } = {}) => (
  hasEsitoInvioOrdinePagamentoSap(listaEsiti) && isSapApplicable(applicabilita)
);

// Con SAP attivo "Invio ordine di pagamento" lascia il posto a "Invio ordine di pagamento SAP";
// altrimenti (flag off o pagamento non applicabile) l'esito SAP resta disabilitato.
export const isEsitoInvioDisabilitatoDaSap = ({ esitoDescription, isSapAttivo } = {}) => {
  if (isEsitoInvioOrdinePagamentoSap(esitoDescription)) {
    return !isSapAttivo;
  }
  return isSapAttivo === true
    && EnumService.ESITI.INVIO_ORDINE_PAGAMENTO.includes(normalize(esitoDescription));
};

export const getPagamentoInviatoSap = (datiPagamento) => (
  [datiPagamento?.pagamentoIndennizzo, datiPagamento?.pagamentoSpesa]
    .find((pagamento) => pagamento?.statoPagamento === STATO_PAGAMENTO_INVIATO_SAP) || null
);

// Esito SAP divergente (D) o rigettato dalla banca (R) sul pagamento: trim e case insensitive,
// false se statoEsitoSap manca o non è una stringa (backend che non lo espone: comportamento invariato).
export const isEsitoSapDivergenteORigettato = (pagamento) => {
  const statoEsitoSap = pagamento?.statoEsitoSap;
  return typeof statoEsitoSap === 'string'
    && SAP_ESITI_STORNO_SBLOCCATO.includes(statoEsitoSap.trim().toUpperCase());
};

// Data di invio a SAP: dataModifica del pagamento; null se assente. sapLastEventAt non si usa: è l'ultimo
// evento SAP (export, ritorno C/L/D/R), non l'invio, e se esposto sposterebbe in avanti il limite delle 19.
export const getDataInvioSap = (pagamento) => (
  hasValue(pagamento?.dataModifica) ? pagamento.dataModifica : null
);

// Scaduto dalle 19:00 (ora italiana) del giorno di invio. Senza una data di invio valida lo storno è
// considerato scaduto: uno storno dopo l'invio a SAP senza controllo è il caso da evitare.
export const isStornoSapScaduto = (dataInvio, now = moment()) => {
  if (!hasValue(dataInvio)) {
    return true;
  }
  const sent = toMomentRoma(dataInvio);
  if (!sent.isValid()) {
    return true;
  }
  // Ora di calendario (non startOf('day') + 19h, che sbaglia di un'ora nei giorni di cambio ora).
  const deadline = sent.clone().set({
    hour: SAP_STORNO_ORA_LIMITE,
    minute: 0,
    second: 0,
    millisecond: 0,
  });
  return moment(now).isSameOrAfter(deadline);
};

// Motivo di blocco degli esiti dopo l'invio a SAP, oppure null se l'esito non è bloccato:
// - "Pagamento effettuato" è impostato automaticamente al ritorno da SAP (bloccato anche con esito D/R);
// - "Storno inviato" è consentito fino alle 19:00 del giorno di invio, a qualsiasi ora con esito SAP D/R.
export const getSapEsitoDisabledReasonKey = ({ esito, datiPagamento, now = moment() } = {}) => {
  const pagamentoSap = getPagamentoInviatoSap(datiPagamento);
  if (!pagamentoSap) {
    return null;
  }
  const esitoDescription = normalize(esito?.description);
  if (EnumService.ESITI.PAGAMENTO_EFFETTUATO.includes(esitoDescription)) {
    return OUTCOME_DISABLED_REASON_KEYS.PAGAMENTO_EFFETTUATO_SAP;
  }
  if (EnumService.ESITI.STORNO_INVIATO.includes(esitoDescription)
      && !isEsitoSapDivergenteORigettato(pagamentoSap)
      && isStornoSapScaduto(getDataInvioSap(pagamentoSap), now)) {
    return OUTCOME_DISABLED_REASON_KEYS.STORNO_SAP_SCADUTO;
  }
  return null;
};
